import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Screen from '../../components/common/Screen';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Icon from '../../components/common/Icon';
import NotFound from '../../components/common/NotFound';
import ReCaptchaWidget from '../../components/forms/ReCaptchaWidget';
import BravosSurveyFlow from '../event/BravosSurveyFlow';
import WorkingOnFeedback from '../event/WorkingOnFeedback';
import { apiRequest } from '../../utils';
import '../readiness/PublicReadiness.css';   // reuse the .readiness / results styles

/* Public, unauthenticated Bravos CxO Pulse — "Are your executives ready?". tornea.app/cxo-pulse/<slug>.
   Twin of PublicPulse, aimed at executives. Anonymous: a visitor answers the paginated CxO Pulse
   (quote/visual cards interleaved) and sees instant leadership-readiness feedback IMMEDIATELY.
   Completing it records an anonymous response for aggregate market data (nothing identifiable). Under
   the results they can opt in to have them emailed — that email is captured as a CRM lead (the response
   stays anonymous). Backed by GET + POST /preview + POST /submit (routes/public_cxo_pulse.py); the
   email step is reCAPTCHA-gated. */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_WORKING_MS = 7000;   // let the AI-style "working on your feedback" animation play out

const PublicCxoPulse = () => {
  const { t } = useTranslation();
  const { slug } = useParams();
  const navigate = useNavigate();

  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState('loading');     // loading | ok | notfound
  const [stage, setStage] = useState('questions');     // questions | working | results | email

  const [answers, setAnswers] = useState({});
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [hp, setHp] = useState('');
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);          // { feedback, cta_url }
  const [emailed, setEmailed] = useState(false);

  useEffect(() => {
    if (!slug) { setStatus('notfound'); return undefined; }
    let cancelled = false;
    setStatus('loading');
    apiRequest('GET', `/public-cxo-pulse/${slug}`)
      .then((res) => { if (!cancelled) { setConfig(res); setStatus('ok'); } })
      .catch(() => { if (!cancelled) setStatus('notfound'); });
    return () => { cancelled = true; };
  }, [slug]);

  const questions = useMemo(() => (config && config.questions) || [], [config]);
  const interstitials = useMemo(() => (config && config.interstitials) || [], [config]);
  const recaptchaSiteKey = (config && config.recaptcha_site_key) || null;

  const setAnswer = (id, v) => setAnswers((a) => ({ ...a, [id]: v }));

  const revealAfter = (started, fn) =>
    setTimeout(fn, Math.max(0, MIN_WORKING_MS - (Date.now() - started)));

  // After the last question: compute + show the results (records an anonymous response).
  const computeResults = async () => {
    setFormError(null);
    setStage('working');
    const started = Date.now();
    try {
      const resp = await fetch(`/api/public-cxo-pulse/${slug}/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ submit_token: config.submit_token, answers }),
      });
      if (resp.ok) {
        const data = await resp.json();
        revealAfter(started, () => { setResult(data); setStage('results'); window.scrollTo(0, 0); });
        return;
      }
      revealAfter(started, () => {
        setResult(null); setFormError(t('cxoPulseSubmitError'));
        setStage('results'); window.scrollTo(0, 0);
      });
    } catch (err) {
      revealAfter(started, () => {
        setResult(null); setFormError(t('cxoPulseSubmitError'));
        setStage('results'); window.scrollTo(0, 0);
      });
    }
  };

  const failToEmail = (msg) => {
    setStage('email');
    setCaptchaToken('');   // reCAPTCHA tokens are single-use — force a fresh challenge on retry
    setSubmitting(false);
    if (msg) setFormError(msg);
    window.scrollTo(0, 0);
  };

  // "Email me my results": send the feedback + capture the email as a lead.
  const mailResults = async () => {
    if (!firstName.trim() || !lastName.trim()) { setFormError(t('cxoPulseNameRequired')); return; }
    if (!EMAIL_RE.test(email.trim())) { setFormError(t('cxoPulseEmailInvalid')); return; }
    if (recaptchaSiteKey && !captchaToken) { setFormError(t('completeCaptcha')); return; }
    setSubmitting(true);
    setFormError(null);
    setStage('working');
    const started = Date.now();

    try {
      const resp = await fetch(`/api/public-cxo-pulse/${slug}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          submit_token: config.submit_token,
          answers,
          first_name: firstName,
          last_name: lastName,
          email,
          phone,
          company,
          recaptcha_token: captchaToken,
          hp: hp || '',
        }),
      });
      if (resp.ok) {
        revealAfter(started, () => {
          setEmailed(true); setSubmitting(false); setStage('results'); window.scrollTo(0, 0);
        });
        return;
      }
      const data = await resp.json().catch(() => ({}));
      if (resp.status === 400 && data.error === 'ERR_CAPTCHA') { failToEmail(t('captchaFailed')); return; }
      if (resp.status === 400 && data.errors && data.errors.phone) { failToEmail(t('cxoPulsePhoneInvalid')); return; }
      if (resp.status === 400 && data.errors && data.errors.email) { failToEmail(t('cxoPulseEmailInvalid')); return; }
      failToEmail(t('cxoPulseSubmitError'));
    } catch (err) {
      failToEmail(t('cxoPulseSubmitError'));
    }
  };

  const brandName = (config && config.bu_name) || 'Bravos';
  const accent = (config && config.primary_color) || null;
  const accentStyle = accent ? { '--readiness-accent': accent } : undefined;

  if (status === 'loading') {
    return <Screen><div className="top"><NotFound translationKey="loading" loading={true} /></div></Screen>;
  }
  if (status === 'notfound' || !config) {
    return (
      <Screen><div className="top">
        <Card>
          <Title><Icon name="info" className="me-2" theme="primary" />{t('cxoPulseTitle')}</Title>
          <p className="text-muted">{t('cxoPulseNotFound')}</p>
        </Card>
      </div></Screen>
    );
  }

  return (
    <Screen><div className="top">
      <Card>
        <div className="readiness" style={accentStyle}>
          {config.logo_url && (
            <img src={config.logo_url} alt={brandName} className="readiness__logo" />
          )}

          {/* ---------- stage: questions (paginated + interstitials) ---------- */}
          {stage === 'questions' && (
            <BravosSurveyFlow
              questions={questions}
              interstitials={interstitials}
              answers={answers}
              setAnswer={setAnswer}
              onComplete={computeResults}
              header={(
                <>
                  <Title>{t('cxoPulseTitle')}</Title>
                  <p className="text-muted text-start mx-2">{t('cxoPulseIntro')}</p>
                </>
              )}
            />
          )}

          {/* ---------- stage: working animation ---------- */}
          {stage === 'working' && <WorkingOnFeedback />}

          {/* ---------- stage: results (shown first; email is opt-in) ---------- */}
          {stage === 'results' && (
            result && result.feedback ? (
              <>
                <CxoPulseResults t={t} feedback={result.feedback} ctaUrl={result.cta_url} navigate={navigate} />
                {emailed ? (
                  <p className="text-success mt-3">
                    <Icon name="check" className="me-1" />{t('cxoPulseEmailedNote')}
                  </p>
                ) : (
                  <button type="button" className="bravos-submit bravos-submit--ghost"
                          onClick={() => { setStage('email'); setFormError(null); window.scrollTo(0, 0); }}>
                    <Icon name="envelope" /> {t('cxoPulseEmailNow')}
                  </button>
                )}
              </>
            ) : (
              <div className="text-center py-3">
                <p className="text-danger">{formError || t('cxoPulseSubmitError')}</p>
                <button type="button" className="bravos-submit" onClick={computeResults}>
                  <Icon name="refresh" /> {t('cxoPulseRetry')}
                </button>
              </div>
            )
          )}

          {/* ---------- stage: email opt-in ---------- */}
          {stage === 'email' && (
            <>
              <Title>{t('cxoPulseFinalTitle')}</Title>
              <p className="text-muted text-start mx-2">{t('cxoPulseFinalIntro')}</p>

              <div className="mb-3 text-start">
                <label className="form-label fw-bold" htmlFor="cxo_pulse_first">
                  {t('readinessFirstName')}<span className="text-danger"> *</span>
                </label>
                <input id="cxo_pulse_first" type="text" className="form-control"
                       value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </div>
              <div className="mb-3 text-start">
                <label className="form-label fw-bold" htmlFor="cxo_pulse_last">
                  {t('readinessLastName')}<span className="text-danger"> *</span>
                </label>
                <input id="cxo_pulse_last" type="text" className="form-control"
                       value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </div>
              <div className="mb-3 text-start">
                <label className="form-label fw-bold" htmlFor="cxo_pulse_email">
                  {t('cxoPulseEmailField')}<span className="text-danger"> *</span>
                </label>
                <input id="cxo_pulse_email" type="email" className="form-control"
                       value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div className="mb-3 text-start">
                <label className="form-label fw-bold" htmlFor="cxo_pulse_phone">{t('readinessPhone')}</label>
                <input id="cxo_pulse_phone" type="tel" className="form-control"
                       value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>
              <div className="mb-3 text-start">
                <label className="form-label fw-bold" htmlFor="cxo_pulse_company">{t('readinessCompany')}</label>
                <input id="cxo_pulse_company" type="text" className="form-control"
                       value={company} onChange={(e) => setCompany(e.target.value)} />
              </div>

              {/* Honeypot — hidden from humans. */}
              <div aria-hidden="true"
                   style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}>
                <label htmlFor="cxo_pulse_hp">{t('formLeaveBlank')}</label>
                <input type="text" id="cxo_pulse_hp" tabIndex={-1} autoComplete="off"
                       value={hp} onChange={(e) => setHp(e.target.value)} />
              </div>

              {recaptchaSiteKey && (
                <div className="mb-3 d-flex justify-content-center">
                  <ReCaptchaWidget
                    siteKey={recaptchaSiteKey}
                    onVerify={(token) => setCaptchaToken(token)}
                    onExpire={() => setCaptchaToken('')}
                    onError={() => setCaptchaToken('')}
                  />
                </div>
              )}

              {formError && <div className="alert alert-danger" role="alert">{formError}</div>}

              <button type="button" className="bravos-submit" onClick={mailResults} disabled={submitting}>
                {submitting ? <span className="auth__spinner" /> : <Icon name="envelope" />}
                {t('cxoPulseEmailNow')}
              </button>
              <button type="button" className="bravos-submit bravos-submit--ghost"
                      onClick={() => { setStage('results'); setFormError(null); }}>
                <Icon name="arrow-left" /> {t('back')}
              </button>
            </>
          )}
        </div>
      </Card>
    </div></Screen>
  );
};

const CxoPulseResults = ({ t, feedback, ctaUrl, navigate }) => {
  const blocks = feedback.blocks || [];
  const hero = blocks[0];
  const insights = blocks.slice(1);
  const focus = feedback.focus;

  return (
    <div className="readiness-results">
      <div className="readiness-recorded">
        <Icon name="check" /> {t('cxoPulseResultsTitle')}
      </div>

      {hero && (
        <div className="readiness-hero">
          {hero.bucket && <h2 className="readiness-hero__headline">{hero.bucket}</h2>}
          {hero.feedback && <p className="readiness-hero__text">{hero.feedback}</p>}
        </div>
      )}

      {insights.map((b, i) => (
        <div key={i} className="readiness-insight">
          {b.bucket && <p className="readiness-insight__label">{b.bucket}</p>}
          {b.feedback && <p className="readiness-insight__text">{b.feedback}</p>}
        </div>
      ))}

      {focus && (focus.bravos_value || focus.diagnostic) && (
        <div className="readiness-insight">
          {focus.bucket && <p className="readiness-insight__label">{focus.bucket}</p>}
          <p className="readiness-insight__text">{focus.bravos_value || focus.diagnostic}</p>
        </div>
      )}

      <div className="readiness-cta">
        <p className="readiness-cta__text">{t('cxoPulseRetakeNote')}</p>
        {ctaUrl && (
          <button type="button" className="bravos-submit" onClick={() => navigate(ctaUrl)}>
            <Icon name="arrow-right" /> {t('cxoPulseAccountCta')}
          </button>
        )}
      </div>
    </div>
  );
};

export default PublicCxoPulse;
